// ui.js - UI Controller for DOM manipulation and HUD updates

import { sound } from './sound.js';

export class UIController {
    constructor(gameState) {
        this.gameState = gameState;
        this.elements = this.cacheElements();
        this.setupEventListeners();
    }

    // Cache all DOM elements
    cacheElements() {
        return {
            // HUD elements
            wellnessBar: document.getElementById('wellness-bar'),
            wellnessValue: document.getElementById('wellness-value'),
            scoreValue: document.getElementById('score-value'),
            timerContainer: document.getElementById('timer-container'),
            timerValue: document.getElementById('timer-value'),
            roomName: document.getElementById('room-name'),
            floorName: document.getElementById('floor-name'),

            // Buttons
            mapButton: document.getElementById('map-button'),
            menuButton: document.getElementById('menu-button'),
            settingsButton: document.getElementById('settings-button'),

            // Modals
            taskCard: document.getElementById('task-card'),
            taskTitle: document.getElementById('task-title'),
            taskDescription: document.getElementById('task-description'),
            taskRoom: document.getElementById('task-room'),
            taskDifficulty: document.getElementById('task-difficulty'),
            startTaskButton: document.getElementById('start-task-button'),
            cancelTaskButton: document.getElementById('cancel-task-button'),

            mapOverlay: document.getElementById('map-overlay'),
            mapGrid: document.getElementById('map-grid'),
            floorTabs: document.querySelectorAll('.floor-tab'),
            closeMapButton: document.getElementById('close-map-button'),

            settingsModal: document.getElementById('settings-modal'),
            musicVolume: document.getElementById('music-volume'),
            sfxVolume: document.getElementById('sfx-volume'),
            musicToggle: document.getElementById('music-toggle'),
            sfxToggle: document.getElementById('sfx-toggle'),
            contrastToggle: document.getElementById('contrast-toggle'),
            saveGameButton: document.getElementById('save-game-button'),
            resetGameButton: document.getElementById('reset-game-button'),
            closeSettingsButton: document.getElementById('close-settings-button'),

            endScreen: document.getElementById('end-screen'),
            endTitle: document.getElementById('end-title'),
            endMessage: document.getElementById('end-message'),
            finalScoreValue: document.getElementById('final-score-value'),
            tryAgainButton: document.getElementById('try-again-button'),
            menuButton: document.getElementById('menu-button'),

            mainMenu: document.getElementById('main-menu'),
            newGameButton: document.getElementById('new-game-button'),
            continueGameButton: document.getElementById('continue-game-button'),
            howToPlayButton: document.getElementById('how-to-play-button'),
            menuSettingsButton: document.getElementById('menu-settings-button'),
            creditsButton: document.getElementById('credits-button'),

            howToPlayModal: document.getElementById('how-to-play-modal'),
            closeInstructionsButton: document.getElementById('close-instructions-button')
        };
    }

    // Setup all event listeners
    setupEventListeners() {
        // HUD buttons
        this.elements.mapButton.addEventListener('click', () => {
            console.log('Map button clicked!');
            sound.playTapSound();
            this.showMap();
        });

        this.elements.menuButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onReturnToMenu?.();
        });

        this.elements.settingsButton.addEventListener('click', () => {
            sound.playTapSound();
            this.showSettings();
        });

        // Task card buttons
        this.elements.startTaskButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onStartTask?.();
        });

        this.elements.cancelTaskButton.addEventListener('click', () => {
            sound.playTapSound();
            this.hideTaskCard();
        });

        // Map buttons
        this.elements.closeMapButton.addEventListener('click', () => {
            sound.playTapSound();
            this.hideMap();
        });

        this.elements.floorTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                sound.playTapSound();
                this.switchFloor(tab.dataset.floor);
            });
        });

        // Settings buttons
        this.elements.closeSettingsButton.addEventListener('click', () => {
            sound.playTapSound();
            this.hideSettings();
        });

        this.elements.musicVolume.addEventListener('input', (e) => {
            const volume = e.target.value / 100;
            sound.setMusicVolume(volume);
            this.gameState.settings.musicVolume = e.target.value;
        });

        this.elements.sfxVolume.addEventListener('input', (e) => {
            const volume = e.target.value / 100;
            sound.setSFXVolume(volume);
            this.gameState.settings.sfxVolume = e.target.value;
        });

        this.elements.musicToggle.addEventListener('click', () => {
            sound.playTapSound();
            this.toggleMusic();
        });

        this.elements.sfxToggle.addEventListener('click', () => {
            sound.playTapSound();
            this.toggleSFX();
        });

        this.elements.contrastToggle.addEventListener('click', () => {
            sound.playTapSound();
            this.toggleContrast();
        });

        this.elements.saveGameButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onSaveGame?.();
        });

        this.elements.resetGameButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onResetGame?.();
        });

        // End screen buttons
        this.elements.tryAgainButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onTryAgain?.();
        });

        this.elements.menuButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onReturnToMenu?.();
        });

        // Main menu buttons
        this.elements.newGameButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onNewGame?.();
        });

        this.elements.continueGameButton.addEventListener('click', () => {
            sound.playTapSound();
            this.onContinueGame?.();
        });

        this.elements.howToPlayButton.addEventListener('click', () => {
            sound.playTapSound();
            this.showHowToPlay();
        });

        this.elements.menuSettingsButton.addEventListener('click', () => {
            sound.playTapSound();
            this.showSettings(true); // Opened from main menu
        });

        this.elements.creditsButton.addEventListener('click', () => {
            sound.playTapSound();
            this.showCredits();
        });

        this.elements.closeInstructionsButton.addEventListener('click', () => {
            sound.playTapSound();
            this.hideHowToPlay();
        });
    }

    // HUD Updates
    updateWellness(value) {
        value = Math.max(0, Math.min(100, value));
        this.elements.wellnessBar.style.width = value + '%';
        this.elements.wellnessValue.textContent = Math.floor(value);

        // Color coding
        this.elements.wellnessBar.classList.remove('low', 'medium');
        if (value <= 20) {
            this.elements.wellnessBar.classList.add('low');
        } else if (value <= 50) {
            this.elements.wellnessBar.classList.add('medium');
        }
    }

    updateScore(value) {
        this.elements.scoreValue.textContent = value;
    }

    updateTimer(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        this.elements.timerValue.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    showTimer() {
        this.elements.timerContainer.style.display = 'flex';
    }

    hideTimer() {
        this.elements.timerContainer.style.display = 'none';
    }

    updateRoom(roomName, floorName) {
        this.elements.roomName.textContent = this.capitalizeRoom(roomName);
        this.elements.floorName.textContent = this.capitalizeFloor(floorName);
    }

    // Task Card
    showTaskCard(task) {
        this.elements.taskTitle.textContent = task.title;
        this.elements.taskDescription.textContent = task.description;
        this.elements.taskRoom.textContent = `Room: ${this.capitalizeRoom(task.room)}`;
        this.elements.taskDifficulty.textContent = `Difficulty: ${'★'.repeat(task.difficulty)}${'☆'.repeat(5 - task.difficulty)}`;

        this.showModal(this.elements.taskCard);
        sound.playNotificationSound();
    }

    hideTaskCard() {
        this.hideModal(this.elements.taskCard);
    }

    // Map Overlay
    showMap() {
        console.log('=== showMap called ===');
        console.log('Current room:', this.gameState.house.currentRoom);
        console.log('Current floor:', this.gameState.house.currentFloor);
        this.renderMap(this.gameState.house.currentFloor);
        this.showModal(this.elements.mapOverlay);
    }

    hideMap() {
        this.hideModal(this.elements.mapOverlay);
    }

    switchFloor(floorName) {
        // Update tab active state
        this.elements.floorTabs.forEach(tab => {
            tab.classList.toggle('active', tab.dataset.floor === floorName);
        });

        this.renderMap(floorName);
    }

    renderMap(floorName) {
        console.log(`=== renderMap called for floor: ${floorName} ===`);
        console.log(`Current room in state: ${this.gameState.house.currentRoom}`);
        console.log(`Current floor in state: ${this.gameState.house.currentFloor}`);

        const floors = {
            house: [
                { id: 'kitchen', name: 'Kitchen', col: 1, row: 1 },
                { id: 'hallway', name: 'Hallway', col: 2, row: 1 },
                { id: 'study', name: 'Study', col: 1, row: 2 },
                { id: 'lab', name: 'Lab', col: 2, row: 2 }
            ],
            garden: [
                { id: 'caravan', name: 'Caravan', col: 1, row: 1 },
                { id: 'dome', name: 'Dome', col: 2, row: 1 },
                { id: 'shed', name: 'Shed', col: 1, row: 2 },
                { id: 'lawn', name: 'Lawn', col: 2, row: 2 }
            ]
        };

        const rooms = floors[floorName] || floors.house;
        console.log(`Rendering ${rooms.length} rooms on ${floorName} floor`);

        // Update floor tab active states to match current floor being shown
        this.elements.floorTabs.forEach(tab => {
            tab.classList.toggle('active', tab.dataset.floor === floorName);
        });

        // Clear existing map
        this.elements.mapGrid.innerHTML = '';
        this.elements.mapGrid.style.gridTemplateColumns = 'repeat(2, 1fr)';
        this.elements.mapGrid.style.gridTemplateRows = 'repeat(2, 1fr)';

        // Create room tiles
        rooms.forEach(room => {
            const tile = document.createElement('div');
            tile.className = 'room-tile';
            tile.dataset.room = room.id;

            // Check if room is current location AND we're viewing the correct floor
            const isCurrentRoom = room.id === this.gameState.house.currentRoom;
            const isCurrentFloor = floorName === this.gameState.house.currentFloor;
            const shouldHighlight = isCurrentRoom && isCurrentFloor;

            console.log(`[TILE] "${room.id}" vs current "${this.gameState.house.currentRoom}" | floor "${floorName}" vs "${this.gameState.house.currentFloor}" => ${shouldHighlight}`);

            if (shouldHighlight) {
                tile.classList.add('active');
                console.log('✓ Added active class to room:', room.id);
                // Force visible styling in case CSS isn't applying
                tile.style.setProperty('background-color', '#4CAF50', 'important');
            }

            // Check if room has available tasks
            const hasTask = this.gameState.tasks.available?.some(t => t.room === room.id);
            if (hasTask) {
                tile.classList.add('has-task');
            }

            // Check if room is unlocked
            const isUnlocked = this.gameState.house.unlockedRooms?.includes(room.id);

            const label = document.createElement('span');
            label.textContent = room.name;

            if (!isUnlocked) {
                tile.classList.add('locked');
                tile.style.opacity = '0.5';
                tile.style.cursor = 'not-allowed';

                // Add lock icon
                const lockIcon = document.createElement('div');
                lockIcon.textContent = '🔒';
                lockIcon.style.fontSize = '20px';
                lockIcon.style.marginBottom = '4px';
                tile.appendChild(lockIcon);

                // Add unlock hint
                const unlockHint = this.getUnlockHint(room.id);
                if (unlockHint) {
                    label.textContent = room.name;
                    label.style.fontSize = '10px';
                    const hintSpan = document.createElement('span');
                    hintSpan.textContent = unlockHint;
                    hintSpan.style.fontSize = '8px';
                    hintSpan.style.display = 'block';
                    hintSpan.style.marginTop = '4px';
                    hintSpan.style.color = '#6B6B6B';
                    tile.appendChild(hintSpan);
                }
            }

            tile.appendChild(label);

            tile.addEventListener('click', () => {
                if (isUnlocked) {
                    sound.playTapSound();
                    this.onRoomSelect?.(room.id, floorName);
                    this.hideMap();
                } else {
                    // Show unlock requirement
                    const hint = this.getUnlockHint(room.id);
                    if (hint) {
                        this.showToast(`Locked! ${hint}`);
                    }
                }
            });

            this.elements.mapGrid.appendChild(tile);
        });
    }

    getUnlockHint(roomId) {
        const hints = {
            'caravan': 'Unlock at 50 pts',
            'dome': 'Unlock at 100 pts',
            'shed': 'Unlock at 150 pts',
            'lawn': 'Unlock at 200 pts'
        };
        return hints[roomId] || null;
    }

    // Settings Modal
    showSettings(fromMainMenu = false) {
        // Track if opened from main menu
        this.settingsFromMainMenu = fromMainMenu;

        // Hide main menu if opened from there
        if (fromMainMenu) {
            this.hideModal(this.elements.mainMenu);
        }

        // Update UI with current settings
        this.elements.musicVolume.value = this.gameState.settings.musicVolume;
        this.elements.sfxVolume.value = this.gameState.settings.sfxVolume;
        this.elements.musicToggle.textContent = this.gameState.settings.musicEnabled ? 'ON' : 'OFF';
        this.elements.sfxToggle.textContent = this.gameState.settings.sfxEnabled ? 'ON' : 'OFF';
        this.elements.contrastToggle.textContent = this.gameState.settings.highContrast ? 'ON' : 'OFF';

        this.showModal(this.elements.settingsModal);
    }

    hideSettings() {
        this.hideModal(this.elements.settingsModal);

        // Restore main menu if it was open before
        if (this.settingsFromMainMenu) {
            this.showModal(this.elements.mainMenu);
            this.settingsFromMainMenu = false;
        }
    }

    toggleMusic() {
        this.gameState.settings.musicEnabled = !this.gameState.settings.musicEnabled;
        this.elements.musicToggle.textContent = this.gameState.settings.musicEnabled ? 'ON' : 'OFF';
        sound.toggleMusic(this.gameState.settings.musicEnabled);
    }

    toggleSFX() {
        this.gameState.settings.sfxEnabled = !this.gameState.settings.sfxEnabled;
        this.elements.sfxToggle.textContent = this.gameState.settings.sfxEnabled ? 'ON' : 'OFF';
        sound.toggleSFX(this.gameState.settings.sfxEnabled);
    }

    toggleContrast() {
        this.gameState.settings.highContrast = !this.gameState.settings.highContrast;
        this.elements.contrastToggle.textContent = this.gameState.settings.highContrast ? 'ON' : 'OFF';
        document.body.classList.toggle('high-contrast', this.gameState.settings.highContrast);
    }

    // End Screen
    showEndScreen(title, message, finalScore) {
        this.elements.endTitle.textContent = title;
        this.elements.endMessage.textContent = message;
        this.elements.finalScoreValue.textContent = finalScore;
        this.showModal(this.elements.endScreen);
    }

    hideEndScreen() {
        this.hideModal(this.elements.endScreen);
    }

    // Main Menu
    showMainMenu(hasSave) {
        this.elements.continueGameButton.style.display = hasSave ? 'block' : 'none';
        this.showModal(this.elements.mainMenu);
    }

    hideMainMenu() {
        this.hideModal(this.elements.mainMenu);
    }

    // How To Play
    showHowToPlay() {
        this.hideModal(this.elements.mainMenu); // Hide main menu first
        this.showModal(this.elements.howToPlayModal);
    }

    hideHowToPlay() {
        this.hideModal(this.elements.howToPlayModal);
        this.showModal(this.elements.mainMenu); // Restore main menu
    }

    // Credits
    showCredits() {
        alert('TASKAGOTCHI\n\nAn 8-bit parody game\nInspired by Taskmaster\n\nUnofficial fan project\nNot affiliated with Taskmaster or Channel 4\n\nCreated with love for silly tasks!');
    }

    // Toast notifications
    showToast(message, duration = 3000) {
        const toast = document.createElement('div');
        toast.className = 'toast';
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            bottom: 100px;
            left: 50%;
            transform: translateX(-50%);
            background-color: #1a1a1a;
            color: #F4E8C1;
            padding: 12px 24px;
            border: 3px solid #D4AF37;
            font-family: 'Courier New', monospace;
            font-size: 14px;
            z-index: 2000;
            animation: toastSlide 0.3s ease;
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
            toast.style.animation = 'toastSlide 0.3s ease reverse';
            setTimeout(() => toast.remove(), 300);
        }, duration);
    }

    // Helper: Show modal
    showModal(modal) {
        modal.style.display = 'flex'; // Override inline style
        modal.classList.add('active');
    }

    // Helper: Hide modal
    hideModal(modal) {
        modal.style.display = 'none'; // Set inline style
        modal.classList.remove('active');
    }

    // Helper: Capitalize room name
    capitalizeRoom(room) {
        return room.charAt(0).toUpperCase() + room.slice(1);
    }

    // Helper: Capitalize floor name
    capitalizeFloor(floor) {
        const names = {
            ground: 'Ground Floor',
            top: 'Top Floor',
            garden: 'Garden'
        };
        return names[floor] || floor;
    }
}
